import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Eye, Lock, Users2, Wallet, Bell, BookOpen, Heart } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/* /parents — information page for the parent / carer / supervisor.
   Plain language. Practical. Calm. Indexable. */

const PROMISES = [
    { icon: Shield, title: 'Safe by design', body: 'No behavioural ads, no chat, no public profiles, no name sharing. Built to the ICO Children\u2019s Code.' },
    { icon: Eye, title: 'You stay in control', body: 'Only the parent or carer can sign in, change billing, add or remove a child profile, or change a language.' },
    { icon: Lock, title: 'Minimal data', body: 'A nickname and an age range \u2014 that\u2019s all we ever ask about a child. No legal names, no school, no address, no phone.' },
    { icon: Users2, title: 'Family-shaped', body: 'Up to five child profiles on the Global plan. Parents play and earn points too on paid plans \u2014 it\u2019s a shared game, not a race.' },
    { icon: Wallet, title: 'Transparent pricing', body: 'GBP, no card needed for the free tier, cancel any time from your Account page. PayPal handles every payment.' },
    { icon: Bell, title: 'Respectful nudges', body: 'No streak guilt. No daily push. We email at most a few times a year, only when something genuinely matters.' },
];

const FAQS = [
    {
        q: 'Do I need a card to start?',
        a: 'No. The free tier gives every family Shona Word Search forever, plus a daily quiz on a country of your choice. You only add a card when (and if) you upgrade.',
    },
    {
        q: 'How young is too young?',
        a: 'We design for ages 4 and up. The Seedlings difficulty uses short, picture-friendly grids and three-letter heritage words. Younger children play happily with a parent or carer alongside them.',
    },
    {
        q: 'My child speaks no Shona / Yoruba / Swahili \u2014 will this work?',
        a: 'Yes. Every word is shown with its English meaning, and tapping any foreign word plays a native-style pronunciation. Parents who don\u2019t speak the language often learn it together with the child.',
    },
    {
        q: 'Will my child see adverts?',
        a: 'Free-tier players see family-safe sponsor banners and the occasional AdSense unit, never behavioural ads. Paid plans are 100% advert-free \u2014 every page, every puzzle, every time.',
    },
    {
        q: 'How do I cancel?',
        a: 'Open your Account page, click Cancel subscription, type the word DELETE in the confirmation box. The cancellation takes effect at the end of the current billing period \u2014 no questions, no exit form.',
    },
    {
        q: 'Can grandparents play with my child?',
        a: 'Yes \u2014 in fact we hope they will. Linguistic Quest was inspired by Gogo and Sekuru, the grandparents whose patient teaching gave the founders their first words of Shona. Sit a phone or tablet between you and play together.',
    },
];

export default function Parents() {
    useSEO({
        title: 'For parents & carers — Linguistic Quest',
        description: 'Plain-language guide for parents, carers and supervisors: how Linguistic Quest keeps children safe, what data we collect, how billing works, and how to share the game with grandparents and siblings.',
        path: '/parents',
        image: '/brand/about_family.jpg',
        jsonLdId: 'parents-page',
        jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQS.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
        },
    });

    return (
        <main className="bg-lq-paper" data-testid="page-parents">
            {/* HERO */}
            <section className="relative overflow-hidden grain-bg">
                <div className="hero-orb w-80 h-80 bg-lq-peach-500 -top-16 -right-10" />
                <div className="max-w-5xl mx-auto px-6 pt-14 pb-12 md:pt-20 md:pb-16 relative">
                    <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500" data-testid="parents-eyebrow">For parents &amp; carers</p>
                    <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] text-lq-ink-black" data-testid="parents-h1">
                        You decide. We just make the puzzles.
                    </h1>
                    <p className="mt-5 text-lg text-lq-ink-700 max-w-2xl leading-relaxed">
                        Linguistic Quest is built around the parent, carer or supervisor. The grown-up holds the account; the child plays in their own profile.
                        No one but you can change the plan, add a sibling, or switch languages. This page is the plain-English guide to how that works.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-6 py-3 rounded-full" data-testid="parents-cta-register">
                            Start free
                        </Link>
                        <Link to="/pricing" className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-6 py-3 rounded-full">
                            See pricing
                        </Link>
                    </div>
                </div>
            </section>

            {/* PROMISES GRID */}
            <section className="max-w-6xl mx-auto px-6 py-14 md:py-20" data-testid="parents-promises">
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-lq-ink-900">Six things we promise every household.</h2>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {PROMISES.map((p) => (
                        <div key={p.title} className="bg-white p-6 rounded-2xl border border-lq-ink-100" data-testid={`parents-promise-${p.title.toLowerCase().replace(/\s+/g,'-')}`}>
                            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-lq-purple-100 text-lq-purple-700">
                                <p.icon size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-lq-ink-900">{p.title}</h3>
                            <p className="mt-2 text-sm text-lq-ink-700 leading-relaxed">{p.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SUPERVISION COPY */}
            <section className="bg-lq-purple-50 border-y border-lq-ink-100 py-14 md:py-20">
                <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-[auto,1fr] gap-6 items-start">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white text-lq-peach-600 border-2 border-lq-peach-300 shrink-0">
                        <Heart size={26} />
                    </div>
                    <div>
                        <h2 className="font-display text-2xl md:text-3xl font-semibold text-lq-ink-900" data-testid="parents-supervision-heading">A note on supervision</h2>
                        <p className="mt-3 text-lq-ink-700 leading-relaxed">
                            We design every screen assuming a grown-up is nearby. When the free tier offers a hint or a fresh attempt in exchange for watching a short advert, the prompt always reads:
                            <em>&ldquo;Ask your parent, carer or supervisor before watching a short advert.&rdquo;</em>
                            We never hide the option behind a child-only path, never use scarcity language (&ldquo;last chance&rdquo;, &ldquo;only 5 left&rdquo;), and never push a paid upgrade as the only escape from a stuck puzzle.
                        </p>
                        <p className="mt-3 text-lq-ink-700 leading-relaxed">
                            If you would rather your child never see an advert, the simplest path is the Explorer plan: every puzzle is unlocked, every page is advert-free, and you keep full control of profiles and billing.
                        </p>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-4xl mx-auto px-6 py-14 md:py-20" data-testid="parents-faqs">
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-lq-ink-900">Parent FAQs</h2>
                <div className="mt-6 space-y-3">
                    {FAQS.map((f, i) => (
                        <details key={i} className="bg-white rounded-2xl border border-lq-ink-100 p-5 group" data-testid={`parents-faq-${i}`}>
                            <summary className="font-display text-lg font-semibold text-lq-ink-900 cursor-pointer flex items-start gap-3 list-none">
                                <BookOpen size={18} className="text-lq-purple-500 mt-1 shrink-0" />
                                <span>{f.q}</span>
                            </summary>
                            <p className="mt-3 ml-7 text-lq-ink-700 leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="max-w-3xl mx-auto px-6 py-14 md:py-20 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-semibold text-lq-ink-900">Ready when you are.</h2>
                <p className="mt-3 text-lq-ink-700">Create the family account in under a minute. Add the first child profile when you&rsquo;re ready &mdash; no rush.</p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                    <Link to="/register" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-6 py-3 rounded-full" data-testid="parents-bottom-cta">
                        Create my family account
                    </Link>
                    <Link to="/contact" className="inline-flex items-center gap-2 text-lq-purple-700 hover:text-lq-purple-900 font-semibold px-6 py-3 rounded-full">
                        Talk to a human
                    </Link>
                </div>
            </section>
        </main>
    );
}
